import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChartBarSquareIcon } from '@heroicons/react/24/outline'
import { Layer, Rectangle, ResponsiveContainer, Sankey, Tooltip as RechartsTooltip } from 'recharts'
import type { DepthStats, SankeyDatum, SankeyNode, SubscriptionPlan } from '../types'
import { Skeleton } from './Skeleton'
import { ProBadge } from './UpgradePrompt'
import { trackViewPricing } from '../utils/facebookPixel'
import { trackGAViewPricing } from '../utils/googleAnalytics'

type Props = {
  sankeyData: SankeyDatum
  depthStats: DepthStats
  isLoading?: boolean
  plan: SubscriptionPlan
}

type NodePayload = SankeyNode & { x: number; y: number; width: number; height: number }
type ProgressTab = 'stages' | 'completion'

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload[0]) return null
  
  const data = payload[0]
  const name = data.name || data.payload?.name || 'Unknown'
  const value = data.value || 0
  
  return (
    <div className="bg-app-surface border border-app-line rounded-lg p-2 shadow-lg">
      <p className="text-sm font-medium text-app-ink">{name}</p>
      <p className="text-xs text-app-muted">{value} applications</p>
    </div>
  )
}

const CustomNode = ({
  x,
  y,
  width,
  height,
  payload,
}: {
  x: number
  y: number
  width: number
  height: number
  payload: NodePayload
}) => (
  <Layer>
    <Rectangle
      x={x}
      y={y}
      width={width}
      height={height}
      fill={payload.fill ?? '#38bdf8'}
      stroke={payload.color ?? '#0ea5e9'}
      strokeWidth={1.2}
      radius={2}
    />
    <text
      x={x + width + 6}
      y={y + height / 2}
      textAnchor="start"
      dominantBaseline="middle"
      className="fill-app-ink"
      fontSize={12}
    >
      {payload.name}
    </text>
  </Layer>
)

const ProgressBar = ({
  label,
  count,
  maxCount,
  colorClass = 'from-sky-600 to-sky-400'
}: {
  label: string
  count: number
  maxCount: number
  colorClass?: string
}) => {
  const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0

  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-app-muted w-16 text-right">{label}</span>
      <div className="flex-1 h-5 bg-app-subtle rounded-full overflow-hidden">
        <div
          className={`h-full bg-gradient-to-r ${colorClass} rounded-full transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-sm text-app-ink w-8">{count}</span>
    </div>
  )
}

export const SankeySection = ({ sankeyData, depthStats, isLoading = false, plan }: Props) => {
  const navigate = useNavigate()
  const [progressTab, setProgressTab] = useState<ProgressTab>('stages')
  const isPro = plan === 'pro'

  const maxDepthCount = Math.max(...depthStats.distribution.map((d) => d.count), 1)
  const maxCompletionCount = Math.max(...depthStats.completionDistribution.map((d) => d.count), 1)

  const getDepthLabel = (depth: number) => 
    depth === 0 ? 'No stages' : depth === 1 ? '1 stage' : `${depth} stages`

  const getCompletionColor = (bucket: string) => {
    if (bucket === '100%') return 'from-emerald-600 to-emerald-400'
    if (bucket === '76-99%') return 'from-sky-600 to-sky-400'
    if (bucket === '51-75%') return 'from-sky-600 to-sky-400'
    if (bucket === '26-50%') return 'from-amber-600 to-amber-400'
    if (bucket === '1-25%') return 'from-orange-600 to-orange-400'
    return 'from-slate-600 to-slate-400'
  }

  // Locked overlay for free users
  const LockedOverlay = () => (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-app-surface/80 backdrop-blur-sm rounded-xl">
      <div className="text-center space-y-3">
        <div className="flex justify-center">
          <ChartBarSquareIcon className="h-16 w-16 text-sky-700 dark:text-app-accent" />
        </div>
        <p className="text-lg font-semibold font-display text-app-ink">Advanced Analytics</p>
        <p className="text-sm text-app-muted max-w-xs">
          Unlock Sankey diagrams, progress charts, and detailed insights with Pro.
        </p>
        <button
          onClick={() => {
            trackViewPricing('analytics_locked_overlay', 'advanced_analytics')
            trackGAViewPricing('analytics_locked_overlay', 'advanced_analytics')
            navigate('/pricing')
          }}
          data-event-category="navigation"
          data-event-action="view_pricing"
          data-event-label="analytics_overlay_locked"
          data-destination="/pricing"
          data-source="analytics_locked_overlay"
          data-feature="advanced_analytics"
          className="mt-2 mx-auto flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white transition-all bg-none bg-app-primary hover:bg-app-primary-hover shadow-none"
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401z" clipRule="evenodd" />
          </svg>
          Upgrade to Pro
        </button>
      </div>
    </div>
  )

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* Status Flow Sankey */}
      <section className="card p-4 space-y-3 relative min-h-[280px]">
        {!isPro && <LockedOverlay />}
        <div>
          <p className="text-lg font-semibold font-display text-app-ink flex items-center gap-2">
            Status Flow
            {!isPro && <ProBadge onClick={() => navigate('/pricing')} source="dashboard_analytics" feature="sankey_status_flow" />}
          </p>
          <p className="text-sm text-app-muted">Application journey through statuses</p>
        </div>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-[260px] w-full" />
          </div>
        ) : sankeyData.links.length === 0 ? (
          <p className="text-sm text-app-muted">No data to display.</p>
        ) : (
          <div className="overflow-x-auto">
            <div className={`h-[300px] w-full ${!isPro ? 'blur-sm pointer-events-none' : ''}`}>
              <ResponsiveContainer width="100%" height="100%">
                <Sankey
                  data={sankeyData}
                  dataKey="value"
                  nodePadding={20}
                  nodeWidth={16}
                  linkCurvature={0.5}
                  margin={{ right: 80, left: 10 }}
                  node={<CustomNode x={0} y={0} width={0} height={0} payload={{} as NodePayload} />}
                  link={{ stroke: 'var(--app-chart-link)', strokeOpacity: 0.5 }}
                >
                  <RechartsTooltip content={<CustomTooltip />} />
                </Sankey>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </section>

      {/* Process Progress with Tabs */}
      <section className="card p-4 space-y-3 relative min-h-[280px]">
        {!isPro && <LockedOverlay />}
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-lg font-semibold font-display text-app-ink flex items-center gap-2">
              Process Progress
              {!isPro && <ProBadge onClick={() => navigate('/pricing')} source="dashboard_analytics" feature="process_progress" />}
            </p>
            <p className="text-sm text-app-muted">
              {progressTab === 'stages' ? 'How many stages completed' : 'Completion rate of processes'}
            </p>
          </div>
          {/* Tabs */}
          <div className="flex rounded-lg border border-app-line overflow-hidden">
            <button
              onClick={() => setProgressTab('stages')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                progressTab === 'stages'
                  ? 'bg-app-highlight text-app-accent'
                  : 'bg-app-subtle text-app-muted hover:text-app-ink'
              }`}
            >
              Stages
            </button>
            <button
              onClick={() => setProgressTab('completion')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                progressTab === 'completion'
                  ? 'bg-app-highlight text-app-accent'
                  : 'bg-app-subtle text-app-muted hover:text-app-ink'
              }`}
            >
              Completion %
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-[200px] w-full" />
          </div>
        ) : depthStats.total === 0 ? (
          <p className="text-sm text-app-muted">No data to display.</p>
        ) : (
          <div className={!isPro ? 'blur-sm pointer-events-none' : ''}>
            {/* Stages Tab */}
            {progressTab === 'stages' && (
              <>
                <div className="space-y-2">
                  {depthStats.distribution.map(({ depth, count }) => (
                    <ProgressBar 
                      key={depth} 
                      label={getDepthLabel(depth)} 
                      count={count} 
                      maxCount={maxDepthCount} 
                    />
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-3 pt-3 border-t border-app-line">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-sky-700 dark:text-sky-400">{ depthStats.avgDepth}</p>
                    <p className="text-xs text-app-muted">Avg. stages</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">{depthStats.maxDepth}</p>
                    <p className="text-xs text-app-muted">Max depth</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-app-ink">{depthStats.total}</p>
                    <p className="text-xs text-app-muted">Total apps</p>
                  </div>
                </div>

                {depthStats.maxDepth > 0 && depthStats.maxDepthCompany && (
                  <p className="text-xs text-app-muted text-center">
                    Deepest: <span className="text-app-muted">{depthStats.maxDepthCompany}</span> ({depthStats.maxDepth} stages)
                  </p>
                )}
              </>
            )}

            {/* Completion Tab */}
            {progressTab === 'completion' && (
              <>
                {depthStats.appsWithStages === 0 ? (
                  <p className="text-sm text-app-muted py-4">
                    No applications with defined stages yet.
                  </p>
                ) : (
                  <>
                    <div className="space-y-2">
                      {depthStats.completionDistribution.map(({ bucket, count }) => (
                        <ProgressBar 
                          key={bucket} 
                          label={bucket} 
                          count={count} 
                          maxCount={maxCompletionCount}
                          colorClass={getCompletionColor(bucket)}
                        />
                      ))}
                    </div>

                    <div className="grid grid-cols-3 gap-3 pt-3 border-t border-app-line">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-sky-700 dark:text-sky-400">{depthStats.avgCompletion}%</p>
                        <p className="text-xs text-app-muted">Avg. completion</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">{depthStats.fullyCompleted}</p>
                        <p className="text-xs text-app-muted">Fully completed</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-app-ink">{depthStats.appsWithStages}</p>
                        <p className="text-xs text-app-muted">With stages</p>
                      </div>
                    </div>

                    <p className="text-xs text-app-muted text-center">
                      {depthStats.fullyCompleted} of {depthStats.appsWithStages} processes fully completed ({Math.round((depthStats.fullyCompleted / depthStats.appsWithStages) * 100)}%)
                    </p>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </section>
    </div>
  )
}
